"use client";

import { useEffect } from "react";
import { persistentCartApi } from "@/api/persistentCartApi";
import { deletePersistentCart } from "@/utils/hooks/usePersistentCart";
import { reportToFlare } from "@/utils/reportToFlare";
import { CART_SHARE_UUID } from "../constants/cartShare";
import { logSafeError } from "@/utils/logSafeError";

const LOCK_KEY = `${CART_SHARE_UUID}-lock`;
const UNAVAILABLE_KEY = `${CART_SHARE_UUID}-unavailable`;
const LOCK_TIMEOUT_MS = 10000; // 10 seconds - enough time for API call but not too long if tab crashes

export function usePersistentCartInit() {
  useEffect(() => {
    // Check if we're in the browser environment
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return;
    }

    let cartShareUuid = null;
    try {
      const storedData = localStorage.getItem(CART_SHARE_UUID);
      cartShareUuid = storedData ? JSON.parse(storedData) : null;
    } catch (error) {
      logSafeError("persistent-cart-init-parse", error);
      // Clear corrupted data
      try {
        localStorage.removeItem(CART_SHARE_UUID);
      } catch (e) {
        logSafeError("persistent-cart-init-corrupted", e);
      }
      cartShareUuid = null;
    }

    // Fix: Check if created_at EXISTS (not if it doesn't exist)
    // In useEffect:
    if (cartShareUuid?.created_at) {
      const createdAt = new Date(cartShareUuid?.created_at);
      // Handle async function properly
      handleUuidExpiration(createdAt).catch((error) => {
        logSafeError("persistent-cart-expiration", error);
      });
    }

    if (
      (!cartShareUuid?.uuid || !cartShareUuid?.created_at) &&
      !isPersistentCartUnavailableForSession()
    ) {
      initializeUuidWithLock();
    }
  }, []);
}

export async function initializeUuidWithLock(): Promise<void> {
  // Check if we're in the browser environment
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return;
  }

  try {
    // Check for existing lock
    const existingLock = localStorage.getItem(LOCK_KEY);
    if (existingLock) {
      const lockTimestamp = parseInt(existingLock, 10);
      const now = Date.now();

      // Check if lock is stale (older than timeout)
      if (now - lockTimestamp < LOCK_TIMEOUT_MS) {
        // Lock is active, another tab is handling initialization
        console.log("UUID initialization already in progress in another tab");
        return;
      } else {
        // Lock is stale, remove it and proceed
        console.log("Removing stale UUID initialization lock");
        try {
          localStorage.removeItem(LOCK_KEY);
        } catch (error) {
          logSafeError("persistent-cart-lock-stale", error);
        }
      }
    }

    // Acquire lock
    const lockTimestamp = Date.now().toString();
    localStorage.setItem(LOCK_KEY, lockTimestamp);

    // Double-check that we actually acquired the lock (race condition protection)
    const verifyLock = localStorage.getItem(LOCK_KEY);
    if (verifyLock !== lockTimestamp) {
      // Another tab acquired the lock first
      console.log("Another tab acquired the UUID initialization lock first");
      return;
    }

    // Proceed with UUID initialization
    await persistentCartApi
      .getUuid()
      .then((r) => {
        if (!r) {
          markPersistentCartUnavailableForSession();
          return;
        }

        // Check if UUID was already set by another tab while we were waiting
        const currentData = localStorage.getItem(CART_SHARE_UUID);
        if (currentData) {
          try {
            const parsed = JSON.parse(currentData);
            if (parsed?.uuid && parsed?.created_at) {
              console.log("UUID already initialized by another tab");
              return;
            }
          } catch (error) {
            logSafeError("persistent-cart-init-existing", error);
          }
        }

        const data = {
          uuid: r.uuid,
          created_at: r.created_at,
        };

        try {
          localStorage.setItem(CART_SHARE_UUID, JSON.stringify(data));
          console.log("UUID successfully initialized and stored");
        } catch (error) {
          logSafeError("persistent-cart-init-save", error);
        }
      })
      .catch((error) => {
        reportToFlare(error, "persistent_cart_uuid_init");
      })
      .finally(() => {
        // Always remove the lock, regardless of success or failure
        try {
          localStorage.removeItem(LOCK_KEY);
        } catch (error) {
          logSafeError("persistent-cart-lock-remove", error);
        }
      });
  } catch (error) {
    logSafeError("persistent-cart-lock-acquire", error);
    // Try to clean up lock on error
    try {
      localStorage.removeItem(LOCK_KEY);
    } catch (cleanupError) {
      logSafeError("persistent-cart-lock-cleanup", cleanupError);
    }
  }
}

function isPersistentCartUnavailableForSession(): boolean {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return false;
  }

  try {
    return sessionStorage.getItem(UNAVAILABLE_KEY) === "true";
  } catch {
    return false;
  }
}

function markPersistentCartUnavailableForSession(): void {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return;
  }

  try {
    sessionStorage.setItem(UNAVAILABLE_KEY, "true");
  } catch {
    // Session storage can be unavailable in strict privacy modes.
  }
}

async function handleUuidExpiration(createdAt: Date): Promise<void> {
  const createdAtMs = createdAt.getTime();

  // Check if UUID should be reinitialized
  const isCorrupted = !Number.isFinite(createdAtMs);
  const isExpired = Date.now() - createdAtMs >= 24 * 60 * 60 * 1000;

  if (!isCorrupted && !isExpired) {
    return; // UUID is still valid
  }

  try {
    await deletePersistentCart();
    // Only reinitialize after successful deletion
    await initializeUuidWithLock();
  } catch (error) {
    logSafeError("persistent-cart-expiration-delete", error);
    // Clear localStorage anyway to prevent using stale data
    try {
      localStorage.removeItem(CART_SHARE_UUID);
    } catch (e) {
      logSafeError("persistent-cart-expiration-clear", e);
    }
    // Reinitialize with fresh state
    await initializeUuidWithLock();
  }
}
